import React, { useState, useEffect } from 'react';
import { useNavigate, Navigate } from 'react-router-dom';
import useStudentAuth from '../../hooks/useStudentAuth';
import { FiMail, FiLock, FiUser } from 'react-icons/fi';

export default function LoginPageStudent() {
  const [formData, setFormData] = useState({
    email: '',
    password: ''
  });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [autoLoginAttempted, setAutoLoginAttempted] = useState(false);
  
  const navigate = useNavigate();
  const { login, isAuthenticated } = useStudentAuth();

  // Admin'den gelen token'ı kontrol et ve otomatik giriş yap
  useEffect(() => {
    const autoLogin = async () => {
      if (autoLoginAttempted) return; // Birden fazla kez çalışmasın
      
      // localStorage'dan token'ı kontrol et
      const adminToken = localStorage.getItem('studentToken');
      const studentInfo = localStorage.getItem('studentInfo');
      
      console.log('Auto login check:', { adminToken, studentInfo });
      
      if (adminToken && studentInfo && !isAuthenticated) {
        try {
          console.log('Admin token found, attempting direct navigation...');
          setAutoLoginAttempted(true);
          
          // StudentInfo'yu temizle ama token kalsin
          localStorage.removeItem('studentInfo');
          
          // Direkt öğrenci ana sayfasına yönlendir
          // StudentAuthProvider token'ı algılayacak
          navigate('/student');
          
        } catch (error) {
          console.error('Otomatik giriş başarısız:', error);
          // Hata durumunda temizle
          localStorage.removeItem('studentToken');
          localStorage.removeItem('studentInfo');
        }
      }
    };
    
    autoLogin();
  }, [navigate, isAuthenticated, autoLoginAttempted]);

  // Eğer zaten giriş yapmışsa ana sayfaya yönlendir
  if (isAuthenticated) {
    return <Navigate to="/student" replace />;
  }

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const result = await login(formData.email, formData.password);
      
      if (result.success) {
        navigate('/student');
      } else {
        setError(result.message || 'Giriş başarısız. Lütfen bilgilerinizi kontrol edin.');
      }
    } catch (err) {
      setError('Bir hata oluştu. Lütfen tekrar deneyin.');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
    // Kullanıcı yazmaya devam ederse error'u temizle
    if (error) setError('');
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-600 via-indigo-700 to-purple-800 relative overflow-hidden">
      {/* Animated Background Elements */}
      <div className="absolute inset-0">
        <div className="absolute top-1/4 right-1/4 w-72 h-72 bg-blue-400/20 rounded-full mix-blend-multiply filter blur-xl animate-bounce"></div>
        <div className="absolute bottom-1/4 left-1/4 w-72 h-72 bg-indigo-400/20 rounded-full mix-blend-multiply filter blur-xl animate-bounce delay-1000"></div>
        <div className="absolute top-1/2 left-1/2 w-72 h-72 bg-purple-400/20 rounded-full mix-blend-multiply filter blur-xl animate-bounce delay-2000"></div>
      </div>

      <div className="relative z-10 min-h-screen flex items-center justify-center p-4">
        <div className="max-w-5xl w-full grid lg:grid-cols-2 gap-12 items-center">

          {/* Left Side - Login Form */}
          <div className="relative order-2 lg:order-1">
            <div className="bg-white/15 backdrop-blur-2xl rounded-3xl p-8 border border-white/20 shadow-2xl">
              <div className="text-center mb-8">
                <div className="text-5xl mb-4">🎓</div>
                <h2 className="text-3xl font-bold text-white mb-2">Öğrenci Girişi</h2>
                <p className="text-blue-100">Öğrenme yolculuğuna devam et!</p>
              </div>

              <form onSubmit={handleSubmit} className="space-y-6">
                {/* Error Message */}
                {error && (
                  <div className="bg-red-500/20 backdrop-blur border border-red-400/50 text-red-100 px-4 py-3 rounded-xl">
                    <span className="block sm:inline">{error}</span>
                  </div>
                )}

                {/* Email Input */}
                <div>
                  <label className="block text-white/90 text-sm font-medium mb-2">
                    📚 E-posta Adresi
                  </label>
                  <div className="relative">
                    <input
                      id="email"
                      name="email"
                      type="email"
                      autoComplete="email"
                      required
                      value={formData.email}
                      onChange={handleChange}
                      className="w-full px-4 py-4 bg-white/10 backdrop-blur border border-white/30 rounded-xl text-white placeholder-white/60 focus:ring-2 focus:ring-blue-400 focus:border-transparent transition-all"
                      placeholder="ogrenci@email.com"
                    />
                    <div className="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none">
                      <div className="text-white/40">💚</div>
                    </div>
                  </div>
                </div>

                {/* Password Input */}
                <div>
                  <label className="block text-white/90 text-sm font-medium mb-2">
                    🔑 Şifre
                  </label>
                  <div className="relative">
                    <input
                      id="password"
                      name="password"
                      type="password"
                      autoComplete="current-password"
                      required
                      value={formData.password}
                      onChange={handleChange}
                      className="w-full px-4 py-4 bg-white/10 backdrop-blur border border-white/30 rounded-xl text-white placeholder-white/60 focus:ring-2 focus:ring-blue-400 focus:border-transparent transition-all"
                      placeholder="••••••••"
                    />
                    <div className="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none">
                      <div className="text-white/40">⭐</div>
                    </div>
                  </div>
                </div>

                {/* Remember & Forgot */}
                <div className="flex items-center justify-between text-sm">
                  <label className="flex items-center text-white/80">
                    <input
                      type="checkbox"
                      className="mr-2 rounded bg-white/10 border-white/30 text-green-400 focus:ring-blue-400"
                    />
                    Beni hatırla
                  </label>
                  <a href="#" className="text-blue-300 hover:text-blue-200 font-medium">
                    Şifremi unuttum?
                  </a>
                </div>

                {/* Submit Button */}
                <button
                  type="submit"
                  disabled={loading}
                  className={`w-full py-4 rounded-xl text-white font-bold text-lg transition-all transform hover:scale-105 ${
                    loading
                      ? 'bg-gray-500/50 cursor-not-allowed'
                      : 'bg-gradient-to-r from-blue-400 via-indigo-500 to-purple-600 hover:from-blue-500 hover:via-indigo-600 hover:to-purple-700 shadow-lg'
                  }`}
                >
                  {loading ? (
                    <div className="flex items-center justify-center">
                      <div className="w-6 h-6 border-2 border-white/30 border-t-white rounded-full animate-spin mr-3"></div>
                      Giriş yapılıyor...
                    </div>
                  ) : (
                    <div className="flex items-center justify-center">
                      <span className="mr-2">🚀</span>
                      Öğrenmeye Başla
                    </div>
                  )}
                </button>
              </form>

              <div className="mt-6 text-center">
                <p className="text-white/60 text-sm">
                  ✨ Bilgi gücün kaynağı
                </p>
              </div>
            </div>
          </div>

          {/* Right Side - Welcome Section */}
          <div className="text-white space-y-8 lg:pl-8 order-1 lg:order-2">
            <div className="space-y-4">
              <div className="flex items-center space-x-3">
                <div className="text-6xl">🌟</div>
                <div>
                  <h1 className="text-5xl font-bold bg-gradient-to-r from-yellow-300 via-blue-300 to-purple-300 bg-clip-text text-transparent">
                    Öğren & Başar
                  </h1>
                  <p className="text-blue-100 text-xl">Hayallerine bir adım daha yakın</p>
                </div>
              </div>
            </div>

            <div className="space-y-6">
              <div className="flex items-center space-x-4">
                <div className="w-12 h-12 bg-gradient-to-r from-yellow-400 to-orange-500 rounded-full flex items-center justify-center text-2xl">
                  📈
                </div>
                <div>
                  <h3 className="text-xl font-semibold">Hızlı Gelişim</h3>
                  <p className="text-blue-100">Kişisel öğrenme rotası</p>
                </div>
              </div>

              <div className="flex items-center space-x-4">
                <div className="w-12 h-12 bg-gradient-to-r from-blue-400 to-cyan-500 rounded-full flex items-center justify-center text-2xl">
                  🎯
                </div>
                <div>
                  <h3 className="text-xl font-semibold">Hedef Odaklı</h3>
                  <p className="text-blue-100">Sınav başarı garantisi</p>
                </div>
              </div>

              <div className="flex items-center space-x-4">
                <div className="w-12 h-12 bg-gradient-to-r from-pink-400 to-purple-500 rounded-full flex items-center justify-center text-2xl">
                  💡
                </div>
                <div>
                  <h3 className="text-xl font-semibold">Akıllı Sistem</h3>
                  <div>
                    <p className="text-blue-100 mb-3">Hızlı okuma Kursu</p>
                    <a
                      href="https://okumaks.com"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center px-4 py-2 bg-gradient-to-r from-blue-400 to-indigo-500 hover:from-blue-500 hover:to-indigo-600 text-white font-medium rounded-lg shadow-lg transform hover:scale-105 transition-all duration-200"
                    >
                      <span className="mr-2">🚀</span>
                      okumaks.com
                    </a>
                  </div> 
                </div>
              </div>
            </div>

            <div className="bg-white/10 backdrop-blur-lg rounded-2xl p-6 border border-white/20">
              <div className="grid grid-cols-3 gap-4 text-center">
                <div>
                  <div className="text-3xl font-bold text-yellow-300">50K+</div>
                  <div className="text-blue-100 text-sm">Başarılı Öğrenci</div>
                </div>
                <div>
                  <div className="text-3xl font-bold text-blue-300">%95</div>
                  <div className="text-blue-100 text-sm">Başarı Oranı</div>
                </div>
                <div>
                  <div className="text-3xl font-bold text-pink-300">500+</div>
                  <div className="text-blue-100 text-sm">Ders Materyali</div>
                </div>
              </div>
            </div>

          </div>
        </div>
      </div>
    </div>
  );
}